import { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import api from '../../hooks/useApi';
import { useToast } from '../ui/Toast';
import { formatDate } from '../../utils/format';

// ── Bringing leave across from Zoho People ───────────────────────────────────
//
// The cutover has to land the leave people had already booked and already
// taken, or every balance in the app is wrong on its first day. Entitlement is
// not imported: the nightly job computes it, and a figure typed over the top is
// one the job contradicts the same night.
//
// Four stages, and nothing live is touched until the third. The server does all
// the reading and resolving; this screen's job is to show what it decided and
// to let somebody say who a name belongs to.
//
// The one thing that genuinely needs a person: the export writes legal first
// names while ops writes what people go by, so "Dharampal Singh" has to reach
// "Paul Singh" and "Charlotte Cushway" has to reach "Lottie Cushway". Neither is
// derivable by any rule, which is why the mapping step is the mechanism rather
// than a safety net on top of it.

const CHUNK = 60;

const RESOLUTION_LABELS = {
  ok: 'Ready to import',
  skipped_status: 'Not approved in Zoho, skipped',
  unmatched_person: 'Person not matched',
  unmapped_type: 'Leave type not mapped',
  unreadable: 'Could not be read',
  duplicate: 'Already in ops',
};

const RESOLUTION_TONE = {
  ok: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  skipped_status: 'bg-gray-50 text-gray-500 border-gray-200',
  unmatched_person: 'bg-amber-50 text-amber-700 border-amber-200',
  unmapped_type: 'bg-amber-50 text-amber-700 border-amber-200',
  unreadable: 'bg-red-50 text-red-700 border-red-200',
  duplicate: 'bg-blue-50 text-blue-700 border-blue-200',
};

export default function LeaveImportPage() {
  const { toast } = useToast();
  const [batchId, setBatchId]   = useState('');
  const [fileName, setFileName] = useState('');
  const [parsing, setParsing]   = useState(false);
  const [staging, setStaging]   = useState(false);
  const [preview, setPreview]   = useState(null);
  const [people, setPeople]     = useState([]);
  const [picks, setPicks]       = useState({});   // source_name -> person_id
  const [busy, setBusy]         = useState('');
  const [error, setError]       = useState('');

  useEffect(() => { api.get('people').then(setPeople).catch(() => {}); }, []);

  const loadPreview = async (id) => {
    try {
      setPreview(await api.get('leave-import/preview', { batch: id }));
    } catch (e) {
      setError(e.message || 'The preview could not be loaded.');
    }
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setParsing(true); setError(''); setPreview(null); setPicks({});
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = async (ev) => {
      let rows = [];
      try {
        const wb = XLSX.read(new Uint8Array(ev.target.result), { type: 'array' });
        // raw:false so the dates arrive as the export wrote them. Read raw they
        // come back as Excel serial numbers and every date is wrong.
        rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: '', raw: false })
          .map((r, i) => ({ ...r, __row: i + 2 }));
      } catch (err) {
        setError('That file could not be read: ' + (err.message || 'unknown error'));
        setParsing(false);
        return;
      }
      setParsing(false);
      if (!rows.length) { setError('That sheet has no rows in it.'); return; }

      const id = `${file.name.replace(/\.[^.]+$/, '')}-${Date.now()}`;
      setBatchId(id);
      setStaging(true);
      try {
        for (let i = 0; i < rows.length; i += CHUNK) {
          await api.post('leave-import/stage', {
            batch_id: id, source_file: file.name, rows: rows.slice(i, i + CHUNK),
          });
        }
        await loadPreview(id);
      } catch (err) {
        setError('Staging failed: ' + (err.message || 'unknown error'));
      }
      setStaging(false);
    };
    reader.readAsArrayBuffer(file);
  };

  const saveMappings = async () => {
    const mappings = Object.entries(picks)
      .filter(([, pid]) => pid)
      .map(([source_name, person_id]) => ({ source_name, person_id }));
    if (!mappings.length) return;
    setBusy('mapping'); setError('');
    try {
      await api.put('leave-import/person-map', { batch_id: batchId, mappings });
      setPicks({});
      await loadPreview(batchId);
      toast.success(`Matched ${mappings.length} name${mappings.length === 1 ? '' : 's'}.`);
    } catch (e) { setError('Saving the mapping failed: ' + (e.message || 'unknown error')); }
    setBusy('');
  };

  const apply = async () => {
    const ready = preview?.counts?.ok || 0;
    if (!window.confirm(`Write ${ready} leave request${ready === 1 ? '' : 's'} into ops? Allowances are recalculated to match, and the whole batch can be rolled back afterwards.`)) return;
    setBusy('apply'); setError('');
    try {
      const r = await api.post('leave-import/apply', { batch_id: batchId });
      await loadPreview(batchId);
      toast.success(`Imported ${r.applied} request${r.applied === 1 ? '' : 's'} for ${r.people_touched} people.`);
    } catch (e) { setError('The import failed: ' + (e.message || 'unknown error')); }
    setBusy('');
  };

  const rollback = async () => {
    if (!window.confirm('Remove everything this batch created and put the allowances back? The staged file is kept, so it can be applied again.')) return;
    setBusy('rollback'); setError('');
    try {
      const r = await api.post('leave-import/rollback', { batch_id: batchId });
      await loadPreview(batchId);
      toast.success(`Removed ${r.removed} request${r.removed === 1 ? '' : 's'}.`);
    } catch (e) { setError('The rollback failed: ' + (e.message || 'unknown error')); }
    setBusy('');
  };

  const appliedCount = (preview?.people || []).reduce((a, p) => a + (p.applied || 0), 0);
  const readyCount = preview?.counts?.ok || 0;

  return (
    <div className="fade-in p-4">
      <div className="mb-4">
        <h1 className="text-base font-extrabold text-navy">Leave Import</h1>
        <p className="text-[10px] text-gray-500 mt-0.5 max-w-3xl">
          Brings leave across from a Zoho People export. Entitlement is not imported: the nightly
          job works that out. Nothing is written until you press Import, and a batch can be rolled
          back afterwards.
        </p>
      </div>

      {/* 1. The file */}
      <div className="bg-white border border-gray-200 rounded p-3 mb-3">
        <div className="text-[10px] font-bold text-navy mb-1">1. Choose the export</div>
        <input type="file" accept=".xls,.xlsx" onChange={onFile} disabled={parsing || staging}
          className="text-[10px] file:mr-2 file:px-2 file:py-1 file:text-[10px] file:font-semibold file:rounded file:border-0 file:bg-navy file:text-white" />
        {(parsing || staging) && (
          <div className="text-[10px] text-gray-500 mt-2">{parsing ? 'Reading the file…' : 'Staging the rows…'}</div>
        )}
        {fileName && !parsing && !staging && (
          <div className="text-[9px] text-gray-400 mt-2">{fileName}{preview ? ` · ${preview.total_rows} rows staged` : ''}</div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded p-2 text-[10px] text-red-700 mb-3">{error}</div>
      )}

      {preview && (
        <>
          {/* 2. What it found */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
            <div className="bg-white border border-gray-200 rounded p-3">
              <div className="text-[8px] uppercase text-gray-500 font-bold">Ready to import</div>
              <div className="text-lg font-extrabold text-navy">{readyCount}</div>
            </div>
            <div className="bg-white border border-gray-200 rounded p-3">
              <div className="text-[8px] uppercase text-gray-500 font-bold">Days</div>
              <div className="text-lg font-extrabold text-navy">{preview.total_days}</div>
            </div>
            <div className="bg-white border border-gray-200 rounded p-3">
              <div className="text-[8px] uppercase text-gray-500 font-bold">People</div>
              <div className="text-lg font-extrabold text-navy">{preview.people.length}</div>
            </div>
            <div className="bg-white border border-gray-200 rounded p-3">
              <div className="text-[8px] uppercase text-gray-500 font-bold">Already imported</div>
              <div className="text-lg font-extrabold text-navy">{appliedCount}</div>
            </div>
          </div>

          <div className="flex flex-wrap gap-1.5 mb-3">
            {Object.entries(preview.counts || {}).map(([k, n]) => (
              <span key={k} className={`text-[9px] font-semibold px-2 py-1 rounded border ${RESOLUTION_TONE[k] || 'bg-gray-50 text-gray-500 border-gray-200'}`}>
                {RESOLUTION_LABELS[k] || k}: {n}
              </span>
            ))}
          </div>

          {/* 3. Names that need a person */}
          {preview.unmatched.length > 0 && (
            <div className="bg-white border border-amber-200 rounded mb-3">
              <div className="px-3 py-2 border-b border-amber-200 bg-amber-50">
                <h3 className="text-[10px] font-bold text-amber-700">Who are these people? ({preview.unmatched.length})</h3>
                <p className="text-[9px] text-amber-700/80 mt-0.5">
                  The export uses legal first names and ops uses the name somebody goes by, so these
                  need saying once. The answer is remembered for the next export.
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead><tr>
                    <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">In the export</th>
                    <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Zoho ID</th>
                    <th className="px-2 py-1 text-center text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Rows</th>
                    <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Is who in ops?</th>
                  </tr></thead>
                  <tbody>
                    {preview.unmatched.map(u => (
                      <tr key={u.source_name} className="border-b border-gray-100">
                        <td className="px-2 py-1.5 text-[10px] font-semibold">{u.source_name}</td>
                        <td className="px-2 py-1.5 text-[9px] text-gray-400">{u.zoho_id || '—'}</td>
                        <td className="px-2 py-1.5 text-[10px] text-center">{u.rows}</td>
                        <td className="px-2 py-1.5">
                          <select className="form-select" value={picks[u.source_name] || ''}
                            onChange={e => setPicks(p => ({ ...p, [u.source_name]: e.target.value }))}>
                            <option value="">Choose a person…</option>
                            {people.filter(p => p.employee_status === 'active')
                              .sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`))
                              .map(p => (
                                <option key={p.id} value={p.id}>
                                  {p.first_name} {p.last_name}{p.preferred_name && p.preferred_name !== p.first_name ? ` (${p.preferred_name})` : ''}
                                </option>
                              ))}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="px-3 py-2 border-t border-gray-200 flex justify-end">
                <button onClick={saveMappings} disabled={busy === 'mapping' || !Object.values(picks).some(Boolean)}
                  className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded disabled:opacity-40">
                  {busy === 'mapping' ? 'Saving…' : 'Save and re-check'}
                </button>
              </div>
            </div>
          )}

          {/* 4. Rows that need a look */}
          {preview.problems.length > 0 && (
            <div className="bg-white border border-gray-200 rounded mb-3">
              <div className="px-3 py-2 border-b border-gray-200">
                <h3 className="text-[10px] font-bold">Rows that will not import ({preview.problems.length})</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead><tr>
                    <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Person</th>
                    <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Zoho type</th>
                    <th className="px-2 py-1 text-center text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Days</th>
                    <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Why</th>
                  </tr></thead>
                  <tbody>
                    {preview.problems.map(p => (
                      <tr key={p.zoho_link_id} className="border-b border-gray-100">
                        <td className="px-2 py-1.5 text-[10px] font-semibold">{p.source_name}</td>
                        <td className="px-2 py-1.5 text-[9px] text-gray-500">{p.source_type}</td>
                        <td className="px-2 py-1.5 text-[10px] text-center">{p.source_days ?? '—'}</td>
                        <td className="px-2 py-1.5 text-[9px] text-gray-500">{p.note}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* 5. Per person */}
          <div className="bg-white border border-gray-200 rounded mb-3">
            <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-[10px] font-bold">What each person gets</h3>
              <span className="text-[8px] text-gray-400">{preview.total_days} days over {readyCount} requests</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead><tr>
                  <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Person</th>
                  <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">In the export as</th>
                  <th className="px-2 py-1 text-center text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Requests</th>
                  <th className="px-2 py-1 text-center text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Days</th>
                  <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">By type</th>
                  <th className="px-2 py-1 text-center text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Imported</th>
                </tr></thead>
                <tbody>
                  {preview.people.map(p => (
                    <tr key={p.person_id} className="border-b border-gray-100 hover:bg-gold/5">
                      <td className="px-2 py-1.5 text-[10px] font-semibold">{p.person_name}</td>
                      <td className="px-2 py-1.5 text-[9px] text-gray-400">{p.source_name}</td>
                      <td className="px-2 py-1.5 text-[10px] text-center">{p.rows}</td>
                      <td className="px-2 py-1.5 text-[10px] text-center font-semibold">{Math.round(p.days * 100) / 100}</td>
                      <td className="px-2 py-1.5">
                        <div className="flex flex-wrap gap-1">
                          {Object.entries(p.by_type).map(([t, d]) => (
                            <span key={t} className="text-[8px] font-semibold px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-100">
                              {t.replace(/_/g, ' ')} {Math.round(d * 100) / 100}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-2 py-1.5 text-[10px] text-center">
                        {p.applied === p.rows && p.rows > 0
                          ? <span className="text-emerald-600 font-bold">all</span>
                          : p.applied > 0 ? `${p.applied}/${p.rows}` : '—'}
                      </td>
                    </tr>
                  ))}
                  {preview.people.length === 0 && (
                    <tr><td colSpan="6" className="text-center py-6 text-[10px] text-gray-400">Nothing resolved cleanly yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* 6. Do it */}
          <div className="bg-white border border-gray-200 rounded p-3 flex flex-wrap items-center justify-between gap-2">
            <div className="text-[9px] text-gray-500 max-w-xl">
              {appliedCount > 0
                ? `${appliedCount} request${appliedCount === 1 ? ' has' : 's have'} been written from this batch. Importing again writes only what is left.`
                : 'Nothing has been written yet. Allowances are recalculated from what lands, so the totals above are what the balances will show.'}
            </div>
            <div className="flex gap-2">
              {appliedCount > 0 && (
                <button onClick={rollback} disabled={!!busy}
                  className="px-3 py-1.5 text-[10px] font-semibold border border-red-200 text-red-600 rounded bg-white disabled:opacity-40">
                  {busy === 'rollback' ? 'Rolling back…' : 'Roll this batch back'}
                </button>
              )}
              <button onClick={apply} disabled={!!busy || readyCount === 0}
                className="px-3 py-1.5 text-[10px] font-semibold bg-navy text-white rounded disabled:opacity-40">
                {busy === 'apply' ? 'Importing…' : `Import ${readyCount} request${readyCount === 1 ? '' : 's'}`}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
